import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    isSupportedPrintImageFile,
    readBlobAsDataUrl,
    MAX_PRINT_IMAGE_FILE_SIZE,
    PRINT_IMAGE_ACCEPT,
} from "./partsSectionPredesign";
import { MASTER_PRINT_TYPE, MASTER_PRINT_FONT_BASE_URL, getAllDesignFonts, getFontFaceUrl } from "../../utils/designerUtils";
import { getSelectionEffectiveSizeCm, PREDESIGN_SIZE_TIERS } from "../../utils/designerHelpers";
import egrEscudoImg from "../../assets/designer/egr-escudo.png";

/**
 * Wizard de inscripciones (prints) — flujo por PASOS que AVANZAN (una pantalla a la vez).
 *
 * Árbol de decisión (pedido del dueño, 2026-07):
 *   Q1 ¿Qué?  →  A) Letras o números   ·   B) Escudo o Dibujo
 *   A → ¿Todos llevan lo mismo?
 *        SÍ  → texto → dónde → colocar (mover/tamaño/tipografía) → técnica → color → revisar
 *        NO  → ¿apodo / número personal / palabra personal? (placeholder APODO/##/PERSONAL)
 *                → dónde → colocar → técnica → color → revisar
 *   B → prediseño/propio → dónde → colocar → técnica → color(«se respetan los del diseño») → revisar
 *   Al confirmar: «¿Querés agregar otro detalle?» → reinicia el ciclo.
 */

const KINDS = [
    { id: "letras", icon: "Aa", title: "Letras o números", desc: "Texto, frase, apodo, número." },
    { id: "escudo", img: egrEscudoImg, title: "Escudo o dibujo", desc: "Logo, imagen o prediseño." },
];

// Personalizado (uno distinto por persona): placeholder que se ve en la prenda.
const PERSONAL_TYPES = [
    { id: "apodo", title: "Apodo", placeholder: "APODO" },
    { id: "numero", title: "Número personal", placeholder: "##" },
    { id: "palabra", title: "Palabra personal", placeholder: "PERSONAL" },
];
const personalPlaceholder = (t) => PERSONAL_TYPES.find((p) => p.id === t)?.placeholder || "";

const techniquesFor = (isEscudo) =>
    isEscudo
        ? [
            { id: "bordado", label: "Bordado", desc: "Hilos cosidos sobre la prenda." },
            { id: "dtf", label: "Estampado", desc: "Impreso, ideal para muchos colores o dibujos complejos." },
        ]
        : [
            { id: "bordado", label: "Bordado", desc: "Hilos cosidos. Ideal para detalles. Hasta 5 cm." },
            { id: "aplique", label: "Aplique de tela", desc: "Tela recortada y cosida. Ideal para letras/números grandes." },
            { id: "dtf", label: "Estampado", desc: "Impreso. Ideal para muchos colores y dibujos." },
        ];

const methodLabel = (method, isEscudo) => techniquesFor(isEscudo).find((t) => t.id === method)?.label || method;

const DEFAULT_FONT_ID = 5; // College (id legacy estable; existe en la tabla fija y en el ABM)

// Inyecta el @font-face de una fuente (una sola vez) para poder previsualizarla. Acepta una URL
// completa (file_url del ABM) o un file relativo que resuelve contra /fonts/master (tabla fija).
const injectedFontFaces = new Set();
const ensureFontFace = (family, fileOrUrl) => {
    if (!family || !fileOrUrl || typeof document === "undefined") return;
    const id = `pw-font-${family}`;
    if (injectedFontFaces.has(family) || document.getElementById(id)) return;
    const src = String(fileOrUrl).startsWith("http") ? String(fileOrUrl) : `${MASTER_PRINT_FONT_BASE_URL}${fileOrUrl}`;
    const format = /\.otf(\?|$)/i.test(src) ? "opentype" : "truetype";
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `@font-face{font-family:'${family}';src:url('${src}') format('${format}');font-weight:normal;font-style:normal;}`;
    document.head.appendChild(style);
    injectedFontFaces.add(family);
};
// Técnica automática por familia de producto (order_item_category_id):
//   Chomba/Remera (1) → estampado.  Buzo/Campera (4) y Pantalón/Short (2) → bordado, y si el
//   diseño supera 5cm salta a aplique.  El resto de categorías no entra al diseñador (default bordado/aplique).
const REMERA_CATEGORY_IDS = new Set([1]);
const APLIQUE_THRESHOLD_CM = 5;
const PRINT_BASE_CM = 4; // alto base del texto a escala 100%
// El texto entra a 5cm (arranca en bordado; si el cliente lo agranda a mano y supera 5cm → aplique).
// El cliente nunca ve cm: solo arrastra. 5cm = base(4) × 125%.
const TEXT_DEFAULT_SIZE_CM = 5;
const TEXT_DEFAULT_SCALE_PCT = String(Math.round((TEXT_DEFAULT_SIZE_CM / PRINT_BASE_CM) * 100)); // "125"
// técnica (key del wizard) → tipo del motor, para actualizar el print en vivo.
const TECH_TYPE = { bordado: MASTER_PRINT_TYPE.EMBROIDERY, aplique: MASTER_PRINT_TYPE.PATCH, dtf: MASTER_PRINT_TYPE.SERIGRAPHY };
const TECH_KEY = { bordado: "bordado", aplique: "aplique", dtf: "estampado" };
const VIEWS = [
    { id: "front", label: "Frente" },
    { id: "back", label: "Espalda" },
    { id: "manga", label: "Mangas" },
];

const emptyDraft = () => ({
    kind: null,          // "letras" | "escudo"
    todos: null,         // true = todos lo mismo · false = personalizado (solo letras)
    personalType: null,  // "apodo" | "numero" | "palabra"
    source: null,        // "prediseno" | "propio" (escudo)
    view: null, method: "bordado",
    methodOverride: null, // el cliente cambió la técnica a mano (bordado ⇄ estampado); null = automática por familia
    text: "", font: DEFAULT_FONT_ID,
    threadColor: null, fabricColor: null, // threadColor = color primario (relleno, slot 0); fabricColor = borde (slot 1)
    borderOn: false,                      // remera: habilitar 2do color (borde)
    predesign: null, image: null, imageName: null,
});

const predesignThumb = (p) => p?.img || p?.icon_url || p?.img_url || p?.img_full || "";
// El mismo diseño está cargado varias veces (una por tamaño/técnica: "Pre 11 (6) bordado",
// "Pre 11 (25) serigrafia"…) pero TODAS comparten la misma imagen. Mostramos uno solo y
// limpiamos el "(tamaño) técnica" del nombre, porque el tamaño ahora es libre y la técnica se deriva.
const predesignName = (p) => {
    const raw = String(p?.nice_name || p?.name || "Prediseño");
    return raw.replace(/\s*\(.*$/, "").trim() || raw;
};
const sameColor = (a, b) => a && b && String(a.code || a.hex || "").toLowerCase() === String(b.code || b.hex || "").toLowerCase();

const Swatch = ({ c, selected, onClick }) => (
    <button type="button" title={c?.name || c?.code} onClick={onClick}
        style={{ width: 30, height: 30, borderRadius: "50%", background: c?.code || c?.hex || "#ccc", border: selected ? "3px solid #2f6fed" : "1px solid rgba(0,0,0,.2)", cursor: "pointer", padding: 0 }} />
);

const cardBtn = (selected) => ({
    textAlign: "left", padding: 14, borderRadius: 12,
    border: selected ? "2px solid #2f6fed" : "1px solid #e0e3ea",
    background: selected ? "#f4f8ff" : "#fff", cursor: "pointer", width: "100%",
});
const gridAuto = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 8 });

const PrintWizard = ({
    colors = [], predesigns = [], predesignsLoading = false, printLocations = [],
    onLoadPredesigns = null, onPrepareLittleDesign = null, onApplyLittleDesign = null, onAddTextPrint = null,
    onSelectPrintFont = null, onSelectPrintColor = null, onSetPrintAttr = null, onClearPrint = null, onConfirm = null,
    onAddImagePrint = null,
    categoryId = null, selectedPrints = {},
}) => {
    const [idx, setIdx] = useState(0);
    const [draft, setDraft] = useState(emptyDraft);
    const [added, setAdded] = useState([]);
    const [uploadError, setUploadError] = useState("");
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState("");
    // Print YA pegado en la prenda (preview en vivo). Se crea al entrar a "Ubicá y ajustá" y se
    // va actualizando (tamaño/tipografía/técnica/color). Al confirmar queda; si se descarta, se borra.
    const [livePrintKey, setLivePrintKey] = useState(null);
    const [fontModalOpen, setFontModalOpen] = useState(false);
    // Solapa de la galería: "prediseno" (dibujos) vs "escudo" (subtype 6). Solo se muestra si hay escudos.
    const [galleryTab, setGalleryTab] = useState("prediseno");
    const fileRef = useRef(null);

    // Fuentes del ABM de V4 (con fallback a la tabla fija). Se arma al montar el wizard; para
    // entonces el diseñador ya las cargó (el fetch arranca al abrirlo, varios pasos antes).
    const FONTS = useMemo(() => getAllDesignFonts().map((f) => ({
        id: f.id, name: f.name, family: f.family, file: f.file, file_url: f.file_url,
    })), []);
    const fontById = useCallback((id) => FONTS.find((f) => f.id === Number(id)) || null, [FONTS]);

    // Al abrir el popup de tipografías, precargar los @font-face de todas para verlas escritas.
    useEffect(() => {
        if (!fontModalOpen) return;
        FONTS.forEach((f) => ensureFontFace(f.family, getFontFaceUrl(f)));
    }, [fontModalOpen, FONTS]);
    // La fuente elegida siempre cargada (para el botón/preview).
    useEffect(() => {
        const f = fontById(draft.font);
        if (f) ensureFontFace(f.family, getFontFaceUrl(f));
    }, [draft.font, fontById]);

    const palette = useMemo(() => (Array.isArray(colors) ? colors.slice(0, 40) : []), [colors]);
    // De-dup por imagen: cada diseño aparece UNA vez (las variantes por tamaño/técnica comparten img).
    const gallery = useMemo(() => {
        const seen = new Set();
        const out = [];
        (Array.isArray(predesigns) ? predesigns : []).forEach((p) => {
            const thumb = predesignThumb(p);
            if (!thumb || seen.has(thumb)) return;
            seen.add(thumb);
            out.push(p);
        });
        return out;
    }, [predesigns]);
    // Escudo = master_print_subtype 6; el resto (5 prediseño, 8 abercrombie) van como "dibujos".
    const isEscudoSubtype = (p) => Number(p?.master_print_subtype ?? p?.subtype ?? 0) === 6;
    const escudoItems = useMemo(() => gallery.filter(isEscudoSubtype), [gallery]);
    const predisenoItems = useMemo(() => gallery.filter((p) => !isEscudoSubtype(p)), [gallery]);
    const hasEscudos = escudoItems.length > 0;
    const visibleGallery = hasEscudos && galleryTab === "escudo" ? escudoItems : (hasEscudos ? predisenoItems : gallery);
    const isEscudo = draft.kind === "escudo";
    const isLetras = draft.kind === "letras";
    const isPersonalizado = isLetras && draft.todos === false;
    // Técnica DERIVADA (el cliente no la elige): por categoría + tamaño REAL del print vivo
    // (que el cliente ajusta a mano sobre la prenda). Texto arranca en 5cm = bordado.
    const isRemera = categoryId != null && REMERA_CATEGORY_IDS.has(Number(categoryId));
    const liveSelection = livePrintKey ? (selectedPrints?.[livePrintKey] || null) : null;
    const liveSizeCm = liveSelection ? getSelectionEffectiveSizeCm(liveSelection) : TEXT_DEFAULT_SIZE_CM;
    // Técnica AUTOMÁTICA por familia = el default que ve el cliente (lo "sugerido").
    const autoMethod = isRemera
        ? "dtf" // remera/chomba → estampado, sea texto o escudo
        : (isEscudo ? "bordado" : (liveSizeCm > APLIQUE_THRESHOLD_CM ? "aplique" : "bordado"));
    const autoBase = autoMethod === "dtf" ? "dtf" : "bordado"; // aplique se muestra como "bordado" en el toggle
    // El cliente puede CAMBIARLA (bordado ⇄ estampado) con el toggle del paso "Color".
    // Si elige "bordado" en un texto grande, el motor igual lo manda a aplique (no se puede bordar
    // tan grande) — el banner de aplique lo explica. Escudo todavía usa la automática (su técnica
    // se hornea al colocar el diseño).
    const derivedMethod = (!isEscudo && draft.methodOverride)
        ? (draft.methodOverride === "dtf"
            ? "dtf"
            : (liveSizeCm > APLIQUE_THRESHOLD_CM ? "aplique" : "bordado"))
        : autoMethod;
    const isAplique = derivedMethod === "aplique";
    const isDtf = derivedMethod === "dtf";
    // ¿El escudo elegido se puede pintar? Flag explícito del escudo (tintable) con fallback a la
    // técnica (bordado tiñe / DTF respeta colores) cuando el escudo no trae el dato.
    const escudoTintableFlag = draft.predesign?.tintable ?? draft.predesign?.can_tint ?? null;
    const escudoTintable = escudoTintableFlag != null ? Number(escudoTintableFlag) === 1 : !isDtf;

    // Texto que se muestra en la prenda: el escrito (todos lo mismo) o el placeholder (personalizado).
    const shownText = isEscudo ? "" : (draft.todos === false ? personalPlaceholder(draft.personalType) : draft.text);

    // Escudo de posición fija (fixed_placement): ya sabe dónde y de qué tamaño va → salteamos "¿Dónde?".
    const isFixedEscudo = isEscudo && Number(draft.predesign?.fixed_placement) === 1;
    // Secuencia de pasos según las decisiones tomadas.
    const steps = useMemo(() => {
        if (!draft.kind) return ["kind"];
        // La técnica ya no es un paso: se deriva de la categoría + tamaño.
        if (isEscudo) return isFixedEscudo
            ? ["kind", "origen", "diseno", "coloca", "color", "revisar"]
            : ["kind", "origen", "diseno", "view", "coloca", "color", "revisar"];
        // letras / números
        if (draft.todos === null) return ["kind", "todos"];
        if (draft.todos === true) return ["kind", "todos", "text", "view", "coloca", "color", "revisar"];
        // personalizado
        if (!draft.personalType) return ["kind", "todos", "personalType"];
        return ["kind", "todos", "personalType", "view", "coloca", "color", "revisar"];
    }, [draft.kind, draft.todos, draft.personalType, isEscudo, isFixedEscudo]);
    const stepKey = steps[Math.min(idx, steps.length - 1)];

    const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
    const next = () => setIdx((i) => i + 1);
    const back = () => setIdx((i) => Math.max(0, i - 1));
    const pick = (patch) => { set(patch); next(); };

    useEffect(() => {
        if (draft.source === "prediseno" && !gallery.length && !predesignsLoading) onLoadPredesigns?.();
    }, [draft.source, gallery.length, predesignsLoading, onLoadPredesigns]);

    // Preview en vivo: al llegar a "Ubicá y ajustá" pegamos el texto/placeholder en la prenda
    // (lado según la vista, blanco por default) para que el cliente lo vea y lo manipule.
    useEffect(() => {
        if (stepKey !== "coloca" || !isLetras || livePrintKey || !onAddTextPrint) return;
        let cancelled = false;
        (async () => {
            setApplying(true);
            try {
                const res = await onAddTextPrint({
                    locationKey: printLocations?.[0]?.key ?? null,
                    view: draft.view,
                    technique: derivedMethod === "dtf" ? "estampado" : derivedMethod,
                    isPersonalText: draft.todos === false,
                    text: shownText,
                    colorIds: [],
                });
                if (!cancelled && res?.printKey) {
                    setLivePrintKey(res.printKey);
                    if (onSelectPrintFont) onSelectPrintFont(res.printKey, draft.font);
                    if (onSetPrintAttr) onSetPrintAttr(res.printKey, { scale_pct: TEXT_DEFAULT_SCALE_PCT });
                }
            } catch { /* si falla, el usuario puede reintentar en el paso siguiente */ }
            if (!cancelled) setApplying(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepKey, isLetras, livePrintKey]);

    // Preview en vivo del PREDISEÑO: al llegar a "Ubicá y ajustá" lo pegamos en la prenda para que
    // el cliente lo vea, lo mueva, lo agrande y lo pueda borrar (antes solo aparecía al confirmar).
    useEffect(() => {
        if (stepKey !== "coloca" || !isEscudo || livePrintKey) return;
        let cancelled = false;
        (async () => {
            setApplying(true);
            try {
                const locationKey = printLocations?.[0]?.key ?? null;
                if (draft.source === "prediseno" && draft.predesign && onPrepareLittleDesign && onApplyLittleDesign) {
                    const side = draft.view === "back" ? 1 : 0;
                    const prepared = await onPrepareLittleDesign(locationKey, draft.predesign, { useEstampado: isDtf, side });
                    if (prepared && !cancelled) {
                        const createdKey = await onApplyLittleDesign(prepared, [], PREDESIGN_SIZE_TIERS.SMALL);
                        if (!cancelled && createdKey) setLivePrintKey(createdKey);
                    }
                } else if (draft.source === "propio" && draft.image && onAddImagePrint) {
                    const res = await onAddImagePrint({ locationKey, view: draft.view, imageUrl: draft.image });
                    if (!cancelled && res?.printKey) setLivePrintKey(res.printKey);
                }
            } catch { /* si falla, el confirm lo reintenta */ }
            if (!cancelled) setApplying(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepKey, isEscudo, livePrintKey, draft.source, draft.predesign, draft.image]);

    // La técnica derivada (y el borde) se empujan solos al print vivo cuando cambian tamaño/categoría/borde.
    useEffect(() => {
        if (isEscudo || !livePrintKey || !onSetPrintAttr) return;
        const hasBorder = derivedMethod === "aplique" || (isDtf && draft.borderOn);
        onSetPrintAttr(livePrintKey, {
            resolved_master_print_type: String(TECH_TYPE[derivedMethod]),
            resolved_technique: TECH_KEY[derivedMethod],
            technique_locked: "1",
            text_border: hasBorder ? "1" : "0",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [derivedMethod, draft.borderOn, livePrintKey]);

    // Sync inverso: el color del print también se puede cambiar tocando el diseño SOBRE la prenda
    // (popover del canvas). Reflejamos ese color real en los swatches del menú de la derecha, para
    // que lo elegido en un lado quede marcado en el otro (antes había que volver a elegirlo acá).
    const liveColor1Id = liveSelection?.attrs?.color1 ?? liveSelection?.attrs?.color ?? null;
    const liveColor2Id = liveSelection?.attrs?.color2 ?? null;
    useEffect(() => {
        if (!liveSelection) return;
        const findById = (id) => {
            const key = String(id ?? "").trim();
            if (!key) return null;
            return palette.find((c) => String(c.id ?? "") === key) || null;
        };
        const c1 = findById(liveColor1Id);
        // El 2do color solo aplica cuando hay borde/aplique (evita re-poblarlo si el cliente lo quitó).
        const c2 = (isAplique || draft.borderOn) ? findById(liveColor2Id) : null;
        setDraft((d) => {
            const patch = {};
            if (c1 && !sameColor(c1, d.threadColor)) patch.threadColor = c1;
            if (c2 && !sameColor(c2, d.fabricColor)) patch.fabricColor = c2;
            return Object.keys(patch).length ? { ...d, ...patch } : d;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveColor1Id, liveColor2Id, isAplique, draft.borderOn, palette]);

    // Helpers de actualización en vivo del print ya pegado.
    const liveFont = (fid) => { if (livePrintKey && onSelectPrintFont) onSelectPrintFont(livePrintKey, fid); };
    const liveColor = (c, index = 0) => { if (livePrintKey && onSelectPrintColor) onSelectPrintColor(livePrintKey, c, index); };

    const onPickFile = async (e) => {
        setUploadError("");
        const file = e.target.files?.[0];
        if (!file) return;
        if (!isSupportedPrintImageFile(file)) return setUploadError("Formato no soportado. Usá PNG, JPG, SVG o WEBP.");
        if (file.size > MAX_PRINT_IMAGE_FILE_SIZE) return setUploadError("La imagen supera los 5 MB.");
        try {
            const dataUrl = await readBlobAsDataUrl(file);
            set({ image: dataUrl, imageName: file.name, predesign: null });
            next();
        } catch { setUploadError("No se pudo leer el archivo."); }
    };

    const draftLabel = () => {
        if (isEscudo) return draft.source === "prediseno" ? (draft.predesign ? predesignName(draft.predesign) : "Prediseño") : (draft.imageName || "Diseño propio");
        if (isPersonalizado) return `${PERSONAL_TYPES.find((p) => p.id === draft.personalType)?.title || "Personalizado"} (${personalPlaceholder(draft.personalType)})`;
        return draft.text.trim() || "Texto";
    };

    // ¿Se puede avanzar en los pasos con varios inputs?
    const canContinue = () => {
        if (stepKey === "text") return !!draft.text.trim();
        if (stepKey === "coloca") return true; // font/size tienen default
        if (stepKey === "color") {
            if (isEscudo) return true;
            // slot 0 = color primario (threadColor); slot 1 = borde (fabricColor)
            if (isAplique) return !!draft.threadColor && !!draft.fabricColor;
            return !!draft.threadColor;
        }
        return true;
    };
    const stepNeedsContinue = ["text", "coloca", "color"].includes(stepKey);

    const confirm = async () => {
        setApplyError("");
        if (isEscudo && draft.source === "prediseno" && draft.predesign && !livePrintKey && onPrepareLittleDesign && onApplyLittleDesign) {
            setApplying(true);
            try {
                const side = draft.view === "back" ? 1 : 0; // manga → frente (0)
                const locationKey = printLocations?.[0]?.key ?? null;
                const prepared = await onPrepareLittleDesign(locationKey, draft.predesign, { useEstampado: isDtf, side });
                if (!prepared) { setApplyError("No hay una ubicación de print disponible en esta prenda."); setApplying(false); return; }
                const colorIds = draft.threadColor?.id ? [String(draft.threadColor.id)] : [];
                // Escudo/prediseño arranca en 8cm (tier SMALL); después se ajusta a mano sobre la prenda.
                const createdKey = await onApplyLittleDesign(prepared, colorIds, PREDESIGN_SIZE_TIERS.SMALL);
                if (!createdKey) { setApplyError("No se pudo pegar el diseño en la prenda. Reintentá."); setApplying(false); return; }
            } catch { setApplyError("No se pudo pegar el diseño en la prenda. Reintentá."); setApplying(false); return; }
            setApplying(false);
        } else if (isLetras && onAddTextPrint && !livePrintKey) {
            // Fallback: si por algún motivo no se creó en vivo, lo pegamos ahora.
            setApplying(true);
            try {
                const technique = derivedMethod === "dtf" ? "estampado" : derivedMethod;
                // slot 0 = primario (threadColor), slot 1 = borde (fabricColor)
                const colorIds = [draft.threadColor?.id, draft.fabricColor?.id].filter(Boolean).map(String);
                const locationKey = printLocations?.[0]?.key ?? null;
                const res = await onAddTextPrint({ locationKey, view: draft.view, technique, isPersonalText: draft.todos === false, text: shownText, colorIds });
                if (!res) { setApplyError("No se pudo agregar la inscripción a la prenda. Reintentá."); setApplying(false); return; }
            } catch { setApplyError("No se pudo agregar la inscripción a la prenda. Reintentá."); setApplying(false); return; }
            setApplying(false);
        }
        // El print de letras ya quedó pegado (vivo); acá solo finalizamos el ciclo.
        setAdded((prev) => [...prev, { ...draft, method: derivedMethod, id: Date.now(), label: draftLabel() }]);
        onConfirm?.({ ...draft, method: derivedMethod, label: draftLabel() });
        setDraft(emptyDraft()); setUploadError(""); setIdx(0); setLivePrintKey(null);
    };

    const Title = ({ children, sub }) => (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
            <span style={{ flex: "0 0 auto", width: 26, height: 26, borderRadius: "50%", background: "#2f6fed", color: "#fff", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{idx + 1}</span>
            <div>
                <h4 style={{ margin: 0, fontSize: 15, lineHeight: 1.25 }}>{children}</h4>
                {sub && <p style={{ margin: "3px 0 0", color: "#6b7280", fontSize: 13 }}>{sub}</p>}
            </div>
        </div>
    );

    // Vista previa del texto/placeholder tal como caerá en la prenda (blanco, para mover).
    const TextPreview = () => (
        <div style={{ marginTop: 10, padding: 16, borderRadius: 12, background: "#334155", display: "flex", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 26, fontWeight: 700, letterSpacing: 1 }}>{shownText || "—"}</span>
        </div>
    );

    return (
        <section style={{ border: "1px solid #e6e8ef", borderRadius: 14, padding: 14, marginBottom: 14, background: "#fff" }}>
            <h3 style={{ margin: "0 0 10px" }}>Inscripciones</h3>

            <div style={{ display: "flex", gap: 5, marginBottom: 12 }}>
                {steps.map((s, i) => (
                    <div key={s} style={{ flex: 1, height: 5, borderRadius: 999, background: i <= idx ? "#2f6fed" : "#e6e8ef" }} />
                ))}
            </div>

            <div style={{ border: "1px solid #eef0f5", borderRadius: 12, padding: 12, minHeight: 210 }}>
                {/* Q1 — ¿Qué? (2 rectángulos chicos, uno arriba del otro) */}
                {stepKey === "kind" && (
                    <>
                        <Title sub="Elegí qué querés agregar.">¿Qué querés agregar?</Title>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {KINDS.map((k) => {
                                const selected = draft.kind === k.id;
                                return (
                                    <button key={k.id} type="button" style={{ ...cardBtn(selected), display: "flex", alignItems: "center", gap: 14, padding: 14 }}
                                        onClick={() => pick({ kind: k.id, todos: null, personalType: null, method: "bordado" })}>
                                        <span style={{ flex: "0 0 auto", width: 52, height: 52, borderRadius: 12, background: selected ? "#eaf1ff" : "#f4f6fb", border: "1px solid #e6e9f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            {k.img
                                                ? <img src={k.img} alt="" style={{ maxWidth: 40, maxHeight: 26, objectFit: "contain" }} />
                                                : <span style={{ fontSize: 22, fontWeight: 800, color: "#2f6fed" }}>{k.icon}</span>}
                                        </span>
                                        <span style={{ flex: 1 }}>
                                            <strong style={{ display: "block", fontSize: 15 }}>{k.title}</strong>
                                            <small style={{ color: "#6b7280" }}>{k.desc}</small>
                                        </span>
                                        <span style={{ flex: "0 0 auto", color: "#c0c6d2", fontSize: 22, lineHeight: 1 }}>›</span>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* A — ¿Todos llevan lo mismo? */}
                {stepKey === "todos" && (
                    <>
                        <Title sub="¿La misma inscripción para todos, o una distinta por persona?">¿Todos llevan lo mismo?</Title>
                        <div style={gridAuto(140)}>
                            <button type="button" style={cardBtn(draft.todos === true)} onClick={() => pick({ todos: true, personalType: null })}>
                                <strong style={{ display: "block" }}>Sí, todos igual</strong>
                                <small style={{ color: "#6b7280" }}>Un mismo texto/número.</small>
                            </button>
                            <button type="button" style={cardBtn(draft.todos === false)} onClick={() => pick({ todos: false, text: "" })}>
                                <strong style={{ display: "block" }}>No, uno por persona</strong>
                                <small style={{ color: "#6b7280" }}>Apodo, número o palabra personal.</small>
                            </button>
                        </div>
                    </>
                )}

                {/* A · NO — tipo de personalizado */}
                {stepKey === "personalType" && (
                    <>
                        <Title sub="En la prenda se ve un placeholder; cada persona carga el suyo después.">¿Qué lleva cada uno?</Title>
                        <div style={{ display: "grid", gap: 8 }}>
                            {PERSONAL_TYPES.map((p) => (
                                <button key={p.id} type="button" style={cardBtn(draft.personalType === p.id)} onClick={() => pick({ personalType: p.id })}>
                                    <strong style={{ display: "block" }}>{p.title}</strong>
                                    <small style={{ color: "#6b7280" }}>Se muestra como «{p.placeholder}».</small>
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {/* A · SÍ — qué texto */}
                {stepKey === "text" && (
                    <>
                        <Title sub="Lo que va a decir la inscripción.">¿Qué texto o número?</Title>
                        <input value={draft.text} onChange={(e) => set({ text: e.target.value })} placeholder="Ej: PROMO 2026, Los Pumas, 10…"
                            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d0d4dd", fontSize: 15 }} />
                    </>
                )}

                {/* B — origen del diseño */}
                {stepKey === "origen" && (
                    <>
                        <Title sub="¿De dónde sale el diseño?">Origen del diseño</Title>
                        <div style={gridAuto(180)}>
                            <button type="button" style={cardBtn(draft.source === "prediseno")} onClick={() => pick({ source: "prediseno" })}>
                                <strong style={{ display: "block" }}>Elegí un diseño nuestro</strong>
                                <small style={{ color: "#6b7280" }}>Galería Little Blue.</small>
                            </button>
                            <button type="button" style={cardBtn(draft.source === "propio")} onClick={() => pick({ source: "propio" })}>
                                <strong style={{ display: "block" }}>Subí tu propio diseño</strong>
                                <small style={{ color: "#6b7280" }}>Pendiente de revisión por Diseño.</small>
                            </button>
                        </div>
                    </>
                )}

                {stepKey === "diseno" && draft.source === "prediseno" && (
                    <>
                        <Title sub="Tocá el que quieras usar.">Elegí un diseño</Title>
                        {hasEscudos && (
                            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                                {[
                                    { id: "prediseno", label: "Dibujos", count: predisenoItems.length },
                                    { id: "escudo", label: "Escudos", count: escudoItems.length },
                                ].map((t) => {
                                    const active = galleryTab === t.id;
                                    return (
                                        <button key={t.id} type="button" onClick={() => setGalleryTab(t.id)}
                                            style={{
                                                flex: 1, padding: "8px 10px", borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 700,
                                                border: active ? "2px solid #2f6fed" : "1px solid #e0e3ea",
                                                background: active ? "#f4f8ff" : "#fff", color: active ? "#2f6fed" : "#6b7280",
                                            }}>
                                            {t.label} ({t.count})
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {predesignsLoading ? <p style={{ color: "#6b7280", fontSize: 13 }}>Cargando galería…</p>
                            : visibleGallery.length === 0 ? <p style={{ color: "#6b7280", fontSize: 13 }}>No hay diseños disponibles.</p>
                                : (
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))", gap: 8, maxHeight: 260, overflowY: "auto", padding: 4 }}>
                                        {visibleGallery.map((p, i) => (
                                            <button key={p.id || i} type="button" title={predesignName(p)} onClick={() => pick({ predesign: p, image: null, imageName: null, ...(Number(p?.fixed_placement) === 1 ? { view: String(p?.fixed_view) === "back" ? "back" : "front" } : {}) })}
                                                style={{ aspectRatio: "1 / 1", borderRadius: 10, border: draft.predesign === p ? "3px solid #2f6fed" : "1px solid #e0e3ea", background: `#fff url(${predesignThumb(p)}) center/contain no-repeat`, cursor: "pointer", padding: 0 }} />
                                        ))}
                                    </div>
                                )}
                    </>
                )}

                {stepKey === "diseno" && draft.source === "propio" && (
                    <>
                        <Title sub="PNG, JPG, SVG o WEBP · máx 5 MB.">Subí tu diseño</Title>
                        <div style={{ padding: 12, borderRadius: 10, background: "#eef6ff", border: "1px solid #cfe0f5", color: "#1e40af", fontSize: 13, marginBottom: 12 }}>
                            <b>Subí el archivo original de tu escudo o logo</b> (el de mejor calidad que tengas). Con el original lo reproducimos tal cual sobre la prenda.
                            <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#1e3a5f" }}>
                                <li>Ideal: <b>fondo transparente (PNG)</b> o <b>vectorial (SVG)</b>.</li>
                                <li>Si es una foto o captura, que se vea <b>nítida y grande</b>.</li>
                                <li>Nuestro equipo de diseño lo revisa y lo adapta a la prenda.</li>
                            </ul>
                        </div>
                        <input ref={fileRef} type="file" accept={PRINT_IMAGE_ACCEPT} onChange={onPickFile} style={{ display: "none" }} />
                        <button type="button" onClick={() => fileRef.current?.click()}
                            style={{ width: "100%", padding: 18, borderRadius: 12, border: "1.5px dashed #b8c4e0", background: "#f8fafd", color: "#2f6fed", fontWeight: 600, cursor: "pointer" }}>
                            {draft.image ? "Cambiar imagen" : "📷 Elegir imagen"}
                        </button>
                        {uploadError && <p style={{ color: "#c0392b", fontSize: 12, margin: "6px 0 0" }}>{uploadError}</p>}
                        {draft.image && (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                                <img src={draft.image} alt="propio" style={{ width: 60, height: 60, objectFit: "contain", borderRadius: 8, border: "1px solid #e0e3ea" }} />
                                <small style={{ color: "#6b7280", flex: 1, wordBreak: "break-all" }}>{draft.imageName} · pendiente de revisión</small>
                            </div>
                        )}
                    </>
                )}

                {/* Dónde */}
                {stepKey === "view" && (
                    <>
                        <Title sub="¿Dónde va la inscripción?">¿Dónde lo querés?</Title>
                        <div style={gridAuto(120)}>
                            {VIEWS.map((v) => (
                                <button key={v.id} type="button" style={cardBtn(draft.view === v.id)} onClick={() => pick({ view: v.id })}>
                                    <strong>{v.label}</strong>
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {/* Colocar: aparece sobre la prenda; se mueve y se agranda/achica a mano (arrastrando). */}
                {stepKey === "coloca" && (
                    <>
                        <Title sub={isFixedEscudo ? "Este diseño va en una posición y tamaño fijos sobre la prenda." : "Aparece sobre la prenda. Movelo y ajustá el tamaño arrastrándolo directamente ahí. Acá elegís la tipografía."}>{isFixedEscudo ? "Así queda" : "Ubicá y ajustá"}</Title>
                        {!isEscudo && <TextPreview />}
                        {!isEscudo && (
                            <>
                                <label style={{ fontSize: 12, fontWeight: 600, display: "block", margin: "12px 0 4px" }}>Tipografía</label>
                                <button
                                    type="button"
                                    onClick={() => setFontModalOpen(true)}
                                    style={{
                                        width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #d0d4dd",
                                        background: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
                                        justifyContent: "space-between", gap: 10,
                                    }}
                                >
                                    <span style={{ fontFamily: `'${fontById(draft.font)?.family || "inherit"}', sans-serif`, fontSize: 22, lineHeight: 1, color: "#111827" }}>
                                        {fontById(draft.font)?.name || "Elegí una tipografía"}
                                    </span>
                                    <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>Cambiar ▾</span>
                                </button>
                            </>
                        )}
                        <p style={{ fontSize: 12, color: "#6b7280", margin: "12px 0 0" }}>
                            {isFixedEscudo
                                ? "📌 Este diseño tiene una ubicación y un tamaño fijos: no se mueve ni se redimensiona."
                                : "📐 El tamaño lo ajustás arrastrando el diseño sobre la prenda, hasta que se vea como querés."}
                        </p>
                    </>
                )}

                {/* Color (la técnica se deriva sola; acá el cliente solo elige color/es) */}
                {stepKey === "color" && (
                    <>
                        <Title>Color</Title>
                        {/* Técnica: default por familia (bordado en buzos/camperas, estampado en remeras/chombas),
                            pero el cliente la puede cambiar. Mensaje amigable + toggle de 2 opciones. */}
                        {!isEscudo && (
                            <div style={{ padding: 12, borderRadius: 10, background: "#f8fafc", border: "1px solid #e5e9f0", marginBottom: 14 }}>
                                <p style={{ fontSize: 13, color: "#374151", margin: "0 0 8px" }}>
                                    Para esta prenda lo hacemos en <b>{methodLabel(autoMethod, false)}</b> — es lo más común y lo que mejor queda.{" "}
                                    {draft.methodOverride ? "Cambiaste la técnica; podés volver al default cuando quieras." : "Si preferís otra, cambiala acá:"}
                                </p>
                                <div style={{ display: "flex", gap: 8 }}>
                                    {[{ id: "bordado", label: "Bordado" }, { id: "dtf", label: "Estampado" }].map((t) => {
                                        const active = t.id === "dtf" ? isDtf : !isDtf; // aplique cuenta como bordado
                                        const isSuggested = !draft.methodOverride && t.id === autoBase;
                                        return (
                                            <button key={t.id} type="button" onClick={() => set({ methodOverride: t.id })}
                                                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 14,
                                                    border: active ? "2px solid #2f6fed" : "1px solid #d8dce5", background: active ? "#eef4ff" : "#fff", color: active ? "#1e40af" : "#374151" }}>
                                                {t.label}{isSuggested ? "  · sugerido" : ""}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {isEscudo ? (
                            (draft.source === "prediseno" && escudoTintable) ? (
                                <>
                                    <label style={{ fontSize: 12, fontWeight: 600 }}>Color del diseño</label>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>{palette.map((c) => <Swatch key={`e-${c.id || c.code}`} c={c} selected={sameColor(c, draft.threadColor)} onClick={() => { set({ threadColor: c }); liveColor(c, 1); }} />)}</div>
                                </>
                            ) : (
                                <div style={{ padding: 14, borderRadius: 10, background: "#eef6ff", color: "#1e40af", fontSize: 14 }}>
                                    🎨 Se respetan los colores del diseño{draft.source === "propio" ? " que subiste" : ""}.
                                </div>
                            )
                        ) : isAplique ? (
                            <>
                                <div style={{ padding: 12, borderRadius: 10, background: "#fff7ed", color: "#9a3412", fontSize: 13, marginBottom: 10, border: "1px solid #fed7aa" }}>
                                    Por el tamaño elegido, este diseño se hace en <b>técnica de aplique</b>: elegí el color de la tela y el del borde.
                                </div>
                                <label style={{ fontSize: 12, fontWeight: 600 }}>Color de la tela</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "4px 0 10px" }}>{palette.map((c) => <Swatch key={`f-${c.id || c.code}`} c={c} selected={sameColor(c, draft.threadColor)} onClick={() => { set({ threadColor: c }); liveColor(c, 1); }} />)}</div>
                                <label style={{ fontSize: 12, fontWeight: 600 }}>Color del borde</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>{palette.map((c) => <Swatch key={`b-${c.id || c.code}`} c={c} selected={sameColor(c, draft.fabricColor)} onClick={() => { set({ fabricColor: c }); liveColor(c, 2); }} />)}</div>
                            </>
                        ) : (
                            <>
                                <label style={{ fontSize: 12, fontWeight: 600 }}>{isDtf ? "Color principal" : "Color del hilo"}</label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "4px 0 10px" }}>{palette.map((c) => <Swatch key={`t-${c.id || c.code}`} c={c} selected={sameColor(c, draft.threadColor)} onClick={() => { set({ threadColor: c }); liveColor(c, 1); }} />)}</div>
                                {isDtf && !draft.borderOn && (
                                    <button type="button" onClick={() => set({ borderOn: true })}
                                        style={{ marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px dashed #2f6fed", background: "#f4f8ff", color: "#2f6fed", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                                        + Habilitar 2do color (borde)
                                    </button>
                                )}
                                {isDtf && draft.borderOn && (
                                    <>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
                                            <label style={{ fontSize: 12, fontWeight: 600 }}>Color del borde</label>
                                            <button type="button" onClick={() => set({ borderOn: false, fabricColor: null })}
                                                style={{ border: 0, background: "transparent", color: "#c0392b", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Quitar 2do color</button>
                                        </div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>{palette.map((c) => <Swatch key={`b-${c.id || c.code}`} c={c} selected={sameColor(c, draft.fabricColor)} onClick={() => { set({ fabricColor: c }); liveColor(c, 2); }} />)}</div>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                {stepKey === "revisar" && (
                    <>
                        <Title>Revisá antes de agregar</Title>
                        <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
                            <div><b>Inscripción:</b> {draftLabel()}</div>
                            <div><b>Técnica:</b> {methodLabel(derivedMethod, isEscudo)}</div>
                            {!isEscudo && <div><b>Tipografía:</b> {FONTS.find((f) => f.id === draft.font)?.name || draft.font}</div>}
                            <div><b>Dónde:</b> {VIEWS.find((v) => v.id === draft.view)?.label || "—"}</div>
                            {isEscudo && <div><b>Color:</b> se respetan los del diseño</div>}
                            {!isEscudo && isAplique && <div><b>Tela / borde:</b> {draft.threadColor?.name || "—"} / {draft.fabricColor?.name || "—"}</div>}
                            {!isEscudo && !isAplique && <div><b>Color{isDtf && draft.borderOn ? " / borde" : ""}:</b> {draft.threadColor?.name || "—"}{isDtf && draft.borderOn ? ` / ${draft.fabricColor?.name || "—"}` : ""}</div>}
                        </div>
                    </>
                )}
            </div>

            {applyError && <p style={{ color: "#c0392b", fontSize: 13, margin: "10px 0 0" }}>{applyError}</p>}

            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 12 }}>
                <button type="button" disabled={idx === 0} onClick={back}
                    style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #d0d4dd", background: "#fff", cursor: idx === 0 ? "default" : "pointer", opacity: idx === 0 ? 0.5 : 1 }}>← Atrás</button>
                {stepKey === "revisar" ? (
                    <button type="button" onClick={confirm} disabled={applying}
                        style={{ padding: "10px 18px", borderRadius: 8, border: 0, background: applying ? "#8fca9f" : "#16a34a", color: "#fff", cursor: applying ? "default" : "pointer" }}>
                        {applying ? "Pegando en la prenda…" : "Agregar inscripción"}
                    </button>
                ) : stepNeedsContinue ? (
                    <button type="button" disabled={!canContinue()} onClick={next}
                        style={{ padding: "10px 18px", borderRadius: 8, border: 0, background: canContinue() ? "#2f6fed" : "#b8c4e0", color: "#fff", cursor: canContinue() ? "pointer" : "default" }}>Continuar →</button>
                ) : <span />}
            </div>

            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #eef0f5" }}>
                <strong style={{ fontSize: 13 }}>Inscripciones agregadas ({added.length})</strong>
                {added.length === 0 ? <p style={{ color: "#9aa1ad", fontSize: 13, margin: "6px 0 0" }}>Todavía no agregaste ninguna.</p> : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                        {added.map((p, i) => (
                            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, border: "1px solid #eef0f5", borderRadius: 10 }}>
                                <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#eef2ff", color: "#2f6fed", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                                <span style={{ flex: 1 }}>
                                    <strong style={{ display: "block", fontSize: 13 }}>{p.label}</strong>
                                    <small style={{ color: "#6b7280" }}>{methodLabel(p.method, p.kind === "escudo")} · {VIEWS.find((v) => v.id === p.view)?.label || ""}</small>
                                </span>
                                <button type="button" onClick={() => setAdded((prev) => prev.filter((x) => x.id !== p.id))} style={{ border: "1px solid #eecccc", background: "#fff5f5", color: "#c0392b", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>Borrar</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {fontModalOpen && (
                <div
                    onClick={() => setFontModalOpen(false)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 2147483600, background: "rgba(15,23,42,.5)",
                        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: "min(560px, 96vw)", maxHeight: "82vh", overflowY: "auto", background: "#fff",
                            borderRadius: 16, padding: 18, boxShadow: "0 20px 60px rgba(0,0,0,.3)",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                            <strong style={{ fontSize: 16 }}>Elegí la tipografía</strong>
                            <button type="button" onClick={() => setFontModalOpen(false)}
                                style={{ border: 0, background: "#f1f5f9", borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>✕</button>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
                            {FONTS.map((f) => {
                                const selected = Number(draft.font) === f.id;
                                return (
                                    <button
                                        key={f.id}
                                        type="button"
                                        onClick={() => { set({ font: f.id }); liveFont(f.id); setFontModalOpen(false); }}
                                        title={f.name}
                                        style={{
                                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
                                            minHeight: 92, padding: "14px 10px", borderRadius: 12, cursor: "pointer",
                                            border: selected ? "2px solid #2f6fed" : "1px solid #e0e3ea",
                                            background: selected ? "#f4f8ff" : "#fff",
                                        }}
                                    >
                                        <span style={{ fontFamily: `'${f.family}', sans-serif`, fontSize: 26, lineHeight: 1.1, textAlign: "center", color: "#111827", wordBreak: "break-word" }}>
                                            {f.name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default PrintWizard;
